import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Typography,
  Button,
  Box,
  IconButton,
  CircularProgress,
  Skeleton,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import InfoIcon from '@mui/icons-material/Info';
import AutoModeIcon from '@mui/icons-material/AutoMode';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import { useWebSocket } from '../WebSocketContext';
import SettingsDialog from './SettingsDialog';
import AddActionMenu from '../AddActionMenu';
import WorkflowActionNode from '../WorkflowActionNode';
import WorkflowFlowHeader from '../WorkflowFlowHeader';
import WorkflowFlowAutomationDialog from '../WorkflowFlowAutomationDialog';
import WorkflowConfirmDialogs from '../WorkflowConfirmDialogs';
import WorkflowFlowManageDialogs from '../WorkflowFlowManageDialogs';
import { isActionEnabled } from '../../Utils/actionSettingsUtils';

// Import icons needed for displaying existing settings
import icon_genai from '../../images/flow_genai.png'
import icon_createfile from '../../images/flow_createfile.png';
import icon_copyfile from '../../images/flow_copyfile.png';
import icon_createfolder from '../../images/folder.png';
import icon_createdocx from '../../images/docx.png';
import icon_createexcel from '../../images/excelexport.png';
import icon_createpdf from '../../images/flow_createpdf.png'; // Reusing doc icon for now
import icon_sendemail from '../../images/flow_sendemail.png';
import icon_callmcp from '../../images/flow_callmcp.png';
import icon_sendapi from '../../images/flow_sendapi.png';
import icon_sendmsgteams from '../../images/flow_teamssendmsg.png';
import icon_createtagbrowseweb from '../../images/flow_createtagbrowseweb.png';
import icon_cmqverifydoctor from '../../images/flow_verifydoctor.png'; // Reusing the same icon for now
import icon_oiiqverifynurse from '../../images/flow_verifynurse.png'; // Reusing the same icon for now
import icon_validatelicense from '../../images/validateform.png'; // Icon for rule-based license validation
import icon_cropimage from '../../images/flow_createfile.png'; // Using createfile icon temporarily for crop image
import icon_javascript from '../../images/flow_callmcp.png'; // Using callmcp icon temporarily for javascript
import { AppTooltip } from '../Common';


// Constants for better maintainability
const BUILTIN_SUPPORTED_SETTING_TYPES = new Set([
  'createfile',
  'copyfile',
  'createfolder',
  'createproject',
  'generatewidget',
  'cropimage',
  'createdocx',
  'createdocxtemplate',
  'createdocxtemplatev2',
  'replacewordtext',
  'anonymizepii',
  'createexcelfromtemplate',
  'addpassworddocx',
  'addpasswordexcel',
  'createpdf',
  'createzipfile',
  'createjsonfile',
  'createxmlfile',
  'sendapi',
  'sendOutlookEmail',
  'sendGmailEmail',
  'sendLinkedinPost',
  'sendLinkedinCompanyPost',
  'msteamsSendMessage',
  'callmcp',
  'createtagbrowseweb',
  'browseweb',
  'computeruse',
  'cmqverifydoctor',
  'oiiqverifynurse',
  'generativeanswer',
  'generativeanswerfile',
  'validatereservation',
  'validatedoctorlicense',
  'simplejavascript',
  'manualtag',
  'triggerflow',
  'looptriggerflow',
  'convertdocxtopdf',
  'dashboardsnapshot',
  'weatherapi'
]);

const ICON_MAP = {
  sendapi: icon_sendapi,
  msteamsSendMessage: icon_sendmsgteams,
  callmcp: icon_callmcp,
  browseweb: icon_createtagbrowseweb,
  computeruse: icon_createtagbrowseweb,
  cmqverifydoctor: icon_cmqverifydoctor,
  oiiqverifynurse: icon_oiiqverifynurse,
  createfile: icon_createfile,
  copyfile: icon_copyfile, // Using copyfile icon for copy
  createfolder: icon_createfolder, // Using folder icon for folder creation
  createproject: icon_createfolder, // Using folder icon for project creation
  generatewidget: icon_genai, // Using GenAI icon for widget generation
  cropimage: icon_cropimage, // Using cropimage icon for image cropping
  generativeanswer: icon_genai, // Using GenAI icon for AI generation
  generativeanswerfile: icon_genai, // Using GenAI icon for AI generation
  validatereservation: icon_cmqverifydoctor, // Using verification icon for reservation validation
  validatedoctorlicense: icon_cmqverifydoctor, // Using verification icon for doctor license validation
  createdocx: icon_createdocx,
  manualtag: icon_genai,
  triggerflow: icon_callmcp,
  looptriggerflow: icon_callmcp,
  dashboardsnapshot: icon_genai,
  createdocxtemplate: icon_createdocx, // Using same icon as createdocx
  createdocxtemplatev2: icon_createdocx,
  replacewordtext: icon_createdocx,
  anonymizepii: icon_createdocx,
  createexcelfromtemplate: icon_createexcel,
  addpassworddocx: icon_createdocx, // Using DOCX icon with lock overlay
  addpasswordexcel: icon_createexcel,
  createpdf: icon_createpdf, // Using createpdf icon for PDF
  createzipfile: icon_createfile,
  createjsonfile: icon_createfile,
  createxmlfile: icon_createfile,
  convertdocxtopdf: icon_createpdf, // Using createpdf icon for DOCX to PDF conversion
  simplejavascript: icon_javascript, // Using callmcp icon for JavaScript
  sendOutlookEmail: icon_sendemail,
  sendGmailEmail: icon_sendemail,
  sendLinkedinPost: icon_sendmsgteams,
  sendLinkedinCompanyPost: icon_sendmsgteams,
  weatherapi: icon_sendapi
};

const DEFAULT_ICON = icon_createfile;
const DEFAULT_LOOP_TAG_NAME = 'loopTag';
const TEMPLATE_GUARDED_COMMANDS = new Set([
  'saveActionSettings',
  'deleteExportSettings',
  'toggleFlowEnable',
  'toggleActionScope',
  'flowRename',
  'swapfloworder',
  'createWorkflowFlow',
  'renameWorkflowFlow',
  'updateWorkflowFlowAutomation',
  'deleteWorkflowFlow',
  'swapWorkflowFlowOrder',
  'moveActionToFlow'
]);

// Helper functions
const filterSupportedSettings = (settings, pluginTypeSet = new Set()) =>
  settings.filter(
    (setting) =>
      BUILTIN_SUPPORTED_SETTING_TYPES.has(setting.type) ||
      setting?.isPlugin === true ||
      pluginTypeSet.has(setting.type)
  );

const getSettingIcon = (type) => ICON_MAP[type] || DEFAULT_ICON;

const getNameMap = (t) => ({
  createfile: t("createfile"),
  copyfile: t("copyfile"),
  createfolder: t("createfolder", "Create Folder"),
  createproject: t("createproject", "Create Project"),
  generatewidget: t("widget.update_widget", "Update Widget"),
  cropimage: t("cropimage", "Crop Image"),
  createdocx: t("createdocx"),
  createdocxtemplate: t("workflow.createdocxtemplate", "Create DOCX Template"),
  createdocxtemplatev2: t("workflow.create_docx_template_v2", "Create DOCX Template V2"),
  replacewordtext: t("workflow.replace_word_text", "Replace Word Text"),
  anonymizepii: t("workflow.anonymize_pii", "Anonymize PII"),
  createexcelfromtemplate: t("workflow.create_excel_template", "Create Excel from Template"),
  addpassworddocx: t("workflow.addpassworddocx", "Add Password to DOCX"),
  addpasswordexcel: t("workflow.addpasswordexcel", "Add Password to Excel"),
  createpdf: t("createpdf"),
  createzipfile: t("createzipfile", "Create ZIP Archive"),
  createjsonfile: t("createjsonfile", "Create JSON File"),
  createxmlfile: t("createxmlfile", "Create XML File"),
  sendapi: t("sendapi"),
  sendOutlookEmail: t("workflow.send_outlook_email.title", "Send email (Outlook)"),
  sendGmailEmail: t("workflow.send_gmail_email.title", "Send email (Gmail)"),
  sendLinkedinPost: t("workflow.send_linkedin_post.title", "Publish post (LinkedIn)"),
  sendLinkedinCompanyPost: t("workflow.send_linkedin_company_post.title", "Publish post (LinkedIn Company)"),
  msteamsSendMessage: t("workflow.send_teams_message", "Send Teams Message"),
  callmcp: t("callmcp"),
  browseweb: t("workflow.browseweb"),
  computeruse: t("workflow.computeruse", "Application Use"),
  cmqverifydoctor: t("cmqverifydoctor", "CMQ Verify Doctor"),
  oiiqverifynurse: t("oiiqverifynurse", "OIIQ Verify Nurse"),
  generativeanswer: t("workflow.generativeanswer"),
  generativeanswerfile: t("workflow.generativeanswerfile", "File Generative Answer"),
  validatereservation: t("validatereservation", "Validate Reservation"),
  validatedoctorlicense: t("validatedoctorlicense", "Validate Doctor License"),
  simplejavascript: t("simplejavascript", "Simple JavaScript"),
  manualtag: t("manualtag", "Manual Tag"),
  triggerflow: t("workflow.triggerflow", "Trigger Flow"),
  looptriggerflow: t("workflow.looptriggerflow", "Search and Act"),
  dashboardsnapshot: t("manualSnap.title", "Dashboard Snapshot"),
  convertdocxtopdf: t("workflow.convertdocxtopdf", "Convert DOCX to PDF"),
  weatherapi: t("weatherapi", "Weather API")
});

const getSettingName = (setting, nameMap, pluginNameByType) =>
  setting.name || pluginNameByType[setting.type] || nameMap[setting.type] || "Workflow";

const parsePositiveInteger = (value) => {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
};

const getLoopTriggerTargetFlowId = (setting) => {
  if (setting?.type !== 'looptriggerflow') {
    return null;
  }

  const customData = setting?.customData || setting?.data || {};
  return parsePositiveInteger(customData?.targetFlowId ?? customData?.target_flow_id);
};

const getLoopTriggerTagName = (setting) => {
  if (setting?.type !== 'looptriggerflow') {
    return null;
  }

  const customData = setting?.customData || setting?.data || {};
  const rawValue = customData?.loopTagName ?? customData?.loop_tag_name ?? DEFAULT_LOOP_TAG_NAME;
  const normalizedValue = String(rawValue || '').trim();
  return normalizedValue || DEFAULT_LOOP_TAG_NAME;
};

const hasTagDefinition = (tags, tagName) => {
  const normalizedTagName = String(tagName || '').trim().toLowerCase();
  if (!normalizedTagName || !Array.isArray(tags)) {
    return false;
  }

  return tags.some((tag) => {
    const candidate = tag?.variable ?? tag?.value ?? tag;
    return String(candidate || '').trim().toLowerCase() === normalizedTagName;
  });
};

const normalizeFlowAutomationUnit = (rawUnit) => {
  const normalized = String(rawUnit || '').trim().toLowerCase();
  if (['minute', 'minutes', 'min', 'mins', 'm'].includes(normalized)) {
    return 'minutes';
  }
  if (['hour', 'hours', 'hr', 'hrs', 'h', 'heure', 'heures'].includes(normalized)) {
    return 'hours';
  }
  if (['day', 'days', 'd', 'jour', 'jours'].includes(normalized)) {
    return 'days';
  }
  return 'minutes';
};

const normalizeFlowTriggerMode = (rawMode, fallbackAutoRunEnabled = null) => {
  const normalized = String(rawMode || '').trim().toLowerCase();
  if (['time', 'schedule', 'scheduled', 'auto'].includes(normalized)) {
    return 'time';
  }
  if (['event', 'events', 'on_event', 'on event', 'change'].includes(normalized)) {
    return 'event';
  }
  if (['disabled', 'disable', 'off', 'manual', 'none'].includes(normalized)) {
    return 'disabled';
  }
  return Number(fallbackAutoRunEnabled || 0) === 1 ? 'time' : 'event';
};

const buildFlowAutomationPayload = (triggerMode, frequencyValue, frequencyUnit) => {
  const normalizedTriggerMode = normalizeFlowTriggerMode(triggerMode);
  const isTimeTriggered = normalizedTriggerMode === 'time';
  return {
    trigger_mode: normalizedTriggerMode,
    auto_run_enabled: isTimeTriggered ? 1 : 0,
    auto_run_frequency_value: isTimeTriggered ? frequencyValue : 0,
    auto_run_frequency_unit: isTimeTriggered ? frequencyUnit : 'minutes'
  };
};

const getTriggerVisualConfig = (triggerMode) => {
  if (triggerMode === 'time') {
    return {
      icon: AutoModeIcon,
      iconColor: '#2e7d32',
      borderColor: 'rgba(46, 125, 50, 0.42)',
      background: 'linear-gradient(135deg, #ffffff 0%, #f1f8e9 100%)',
      iconBackground: 'linear-gradient(135deg, rgba(76, 175, 80, 0.2) 0%, rgba(102, 187, 106, 0.1) 100%)',
      shadow: '0 6px 20px rgba(76, 175, 80, 0.22), inset 0 1px 2px rgba(255, 255, 255, 0.55)',
      hoverShadow: '0 10px 26px rgba(76, 175, 80, 0.3), 0 4px 10px rgba(76, 175, 80, 0.16)',
      labelColor: '#2e7d32',
      labelBackground: 'rgba(76, 175, 80, 0.1)',
      labelBorder: 'rgba(76, 175, 80, 0.26)'
    };
  }

  if (triggerMode === 'disabled') {
    return {
      icon: PauseCircleOutlineIcon,
      iconColor: '#6d6d6d',
      borderColor: 'rgba(125, 125, 125, 0.35)',
      background: 'linear-gradient(135deg, #ffffff 0%, #f3f4f6 100%)',
      iconBackground: 'linear-gradient(135deg, rgba(117, 117, 117, 0.18) 0%, rgba(158, 158, 158, 0.08) 100%)',
      shadow: '0 3px 12px rgba(117, 117, 117, 0.16), inset 0 1px 2px rgba(255, 255, 255, 0.55)',
      hoverShadow: '0 8px 18px rgba(117, 117, 117, 0.24), 0 3px 8px rgba(117, 117, 117, 0.14)',
      labelColor: '#616161',
      labelBackground: 'rgba(117, 117, 117, 0.1)',
      labelBorder: 'rgba(117, 117, 117, 0.25)'
    };
  }

  return {
    icon: PlayArrowIcon,
    iconColor: '#1565c0',
    borderColor: 'rgba(21, 101, 192, 0.4)',
    background: 'linear-gradient(135deg, #ffffff 0%, #f1f7ff 100%)',
    iconBackground: 'linear-gradient(135deg, rgba(33, 150, 243, 0.2) 0%, rgba(100, 181, 246, 0.1) 100%)',
    shadow: '0 6px 20px rgba(33, 150, 243, 0.2), inset 0 1px 2px rgba(255, 255, 255, 0.55)',
    hoverShadow: '0 10px 24px rgba(33, 150, 243, 0.3), 0 4px 10px rgba(33, 150, 243, 0.14)',
    labelColor: '#1565c0',
    labelBackground: 'rgba(33, 150, 243, 0.1)',
    labelBorder: 'rgba(33, 150, 243, 0.24)'
  };
};

const WORKFLOW_NODE_CONTAINER_SX = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  mb: 3,
  position: 'relative',
  minWidth: { xs: '108px', md: '150px' },
  pt: '25px',
};

const WorkflowConnector = () => (
  <Box sx={{
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: { xs: '30px', md: '52px' },
    px: { xs: 0.5, md: 1.5 },
    pt: '25px',
    height: { xs: '60px', md: '80px' },
    flexShrink: 0,
  }}>
    <Box sx={{
      position: 'relative',
      width: { xs: '22px', md: '42px' },
      height: '4px',
      background: 'linear-gradient(90deg, rgba(87, 87, 176, 0.32) 0%, rgba(87, 87, 176, 0.72) 50%, rgba(87, 87, 176, 0.32) 100%)',
      borderRadius: '999px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      boxShadow: '0 1px 4px rgba(87, 87, 176, 0.22)',
      overflow: 'visible',
      '&::before': {
        content: '""',
        position: 'absolute',
        left: '-2px',
        top: '50%',
        transform: 'translateY(-50%)',
        width: '4px',
        height: '4px',
        borderRadius: '50%',
        backgroundColor: 'rgba(87, 87, 176, 0.45)',
      },
      '&::after': {
        content: '""',
        position: 'absolute',
        right: '-6px',
        top: '50%',
        transform: 'translateY(-50%)',
        width: 0,
        height: 0,
        borderLeft: '8px solid rgba(87, 87, 176, 0.64)',
        borderTop: '6px solid transparent',
        borderBottom: '6px solid transparent',
        filter: 'drop-shadow(0 1px 2px rgba(87, 87, 176, 0.25))',
      }
    }} />
  </Box>
);

const ActionsDataflowList = ({ caseid, autoOpenExportId, onExportOpened }) => {
  const { t } = useTranslation();
  const { ws, subscribeToMessages } = useWebSocket();
  // State management
  const [settings, setSettings] = useState([]);
  const [selectedSettingId, setSelectedSettingId] = useState(null);
  const [selectedSettingType, setSelectedSettingType] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [replayConfirmOpen, setReplayConfirmOpen] = useState(false);
  const [idToDelete, setIdToDelete] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [isSettingDialogOpen, setIsSettingDialogOpen] = useState(false);
  const [tags, setTags] = useState([]);
  const [runningWorkflows, setRunningWorkflows] = useState(new Set());
  const [isReplayingAllWorkflows, setIsReplayingAllWorkflows] = useState(false);
  const [flows, setFlows] = useState([]);
  const [selectedFlowId, setSelectedFlowId] = useState(null);
  const [replayTimeoutId, setReplayTimeoutId] = useState(null);
  const [currentProcessingStep, setCurrentProcessingStep] = useState(null);
  const [processedSteps, setProcessedSteps] = useState(new Set());
  const [startedSteps, setStartedSteps] = useState(new Set());
  const [togglingScope, setTogglingScope] = useState(null); // Track which action is toggling scope
  const [workflowError, setWorkflowError] = useState(null); // Track workflow execution errors
  const [isLoading, setIsLoading] = useState(true); // Track initial loading state
  const [hasRequestedTags, setHasRequestedTags] = useState(false);
  const [pluginActions, setPluginActions] = useState([]);
  const [flowAutomationDialogOpen, setFlowAutomationDialogOpen] = useState(false);
  const [flowAutomationDialogMode, setFlowAutomationDialogMode] = useState('create');
  const [flowAutomationDialogFlowId, setFlowAutomationDialogFlowId] = useState(null);
  const [flowAutomationDialogName, setFlowAutomationDialogName] = useState('');
  const [flowAutomationDialogTriggerMode, setFlowAutomationDialogTriggerMode] = useState('event');
  const [flowAutomationDialogFrequencyValue, setFlowAutomationDialogFrequencyValue] = useState('1');
  const [flowAutomationDialogFrequencyUnit, setFlowAutomationDialogFrequencyUnit] = useState('minutes');
  const [flowAutomationDialogError, setFlowAutomationDialogError] = useState('');
  const [flowRenameDialogOpen, setFlowRenameDialogOpen] = useState(false);
  const [flowRenameDialogValue, setFlowRenameDialogValue] = useState('');
  const [flowRenameDialogError, setFlowRenameDialogError] = useState('');
  const [flowDeleteDialogOpen, setFlowDeleteDialogOpen] = useState(false);
  const [templateDecisionDialogOpen, setTemplateDecisionDialogOpen] = useState(false);
  const [templateDecisionData, setTemplateDecisionData] = useState(null);
  const [pendingTemplateMutation, setPendingTemplateMutation] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  // Memoized values
  const menuOpen = useMemo(() => Boolean(anchorEl), [anchorEl]);
  const nameMap = useMemo(() => getNameMap(t), [t]);
  const pluginTypeSet = useMemo(
    () => new Set((pluginActions || []).map((plugin) => plugin?.type).filter(Boolean)),
    [pluginActions]
  );
  const pluginNameByType = useMemo(() => {
    const names = {};
    (pluginActions || []).forEach((plugin) => {
      if (plugin?.type) {
        names[plugin.type] = plugin.name || plugin.type;
      }
    });
    return names;
  }, [pluginActions]);
  const currentSettingForDialog = useMemo(() => {
    const existingSetting = settings.find(s => s.id === selectedSettingId);
    if (existingSetting) {
      return existingSetting;
    }

    // If setting not found in list and we have a selectedSettingType, create a temporary setting for the dialog
    if (selectedSettingType && selectedSettingId) {
      return {
        id: selectedSettingId,
        type: selectedSettingType,
        enable: 0,
        caseid: caseid,
        interval: '0',
        name: pluginNameByType[selectedSettingType] || t(`settings.new_${selectedSettingType}_name`, `New ${selectedSettingType.charAt(0).toUpperCase() + selectedSettingType.slice(1)}`),
        scope: 'event', // Default scope for fallback
        flow_id: selectedFlowId ?? (flows[0]?.id ?? null),
        data: {}
      };
    }

    return null;
  }, [settings, selectedSettingId, selectedSettingType, caseid, t, pluginNameByType, selectedFlowId, flows]);

  // Memoized filtered and processed settings
  const processedSettings = useMemo(() => {
    return settings.map(setting => ({
      ...setting,
      isEnabled: isActionEnabled(setting.enable),
      name: getSettingName(setting, nameMap, pluginNameByType),
      iconSrc: getSettingIcon(setting.type)
    }));
  }, [settings, nameMap, pluginNameByType]);

  const sortedFlows = useMemo(() => {
    return [...(flows || [])].sort((a, b) => {
      const orderA = Number.isFinite(Number(a?.orderIndex)) ? Number(a.orderIndex) : 0;
      const orderB = Number.isFinite(Number(b?.orderIndex)) ? Number(b.orderIndex) : 0;
      if (orderA !== orderB) {
        return orderA - orderB;
      }
      return Number(a?.id || 0) - Number(b?.id || 0);
    });
  }, [flows]);

  const fallbackFlowId = useMemo(() => {
    if (selectedFlowId !== null && selectedFlowId !== undefined) {
      return selectedFlowId;
    }
    return sortedFlows[0]?.id ?? null;
  }, [selectedFlowId, sortedFlows]);

  const resolveSettingFlowId = useCallback((setting) => {
    if (!setting) return fallbackFlowId;
    if (setting.flow_id !== undefined && setting.flow_id !== null) {
      return setting.flow_id;
    }
    if (setting.flowId !== undefined && setting.flowId !== null) {
      return setting.flowId;
    }
    return sortedFlows[0]?.id ?? fallbackFlowId;
  }, [sortedFlows, fallbackFlowId]);

  const selectedFlowSettings = useMemo(() => {
    if (!processedSettings.length) {
      return [];
    }
    if (fallbackFlowId === null || fallbackFlowId === undefined) {
      return processedSettings;
    }
    return processedSettings.filter((setting) => String(resolveSettingFlowId(setting)) === String(fallbackFlowId));
  }, [processedSettings, fallbackFlowId, resolveSettingFlowId]);

  const selectedFlow = useMemo(
    () => sortedFlows.find((flow) => String(flow.id) === String(fallbackFlowId)) || null,
    [sortedFlows, fallbackFlowId]
  );

  const dialogTags = useMemo(() => {
    const baseTags = Array.isArray(tags) ? tags : [];
    const dialogFlowId = parsePositiveInteger(
      currentSettingForDialog?.flow_id ?? currentSettingForDialog?.flowId ?? fallbackFlowId
    );

    if (!dialogFlowId) {
      return baseTags;
    }

    const loopTagNames = [...new Set(
      settings
        .filter((setting) => getLoopTriggerTargetFlowId(setting) === dialogFlowId)
        .map((setting) => getLoopTriggerTagName(setting))
        .filter(Boolean)
    )];

    if (loopTagNames.length === 0) {
      return baseTags;
    }

    return [
      ...baseTags,
      ...loopTagNames
        .filter((tagName) => !hasTagDefinition(baseTags, tagName))
        .map((tagName) => ({
          variable: tagName,
          label: tagName,
          description: t(
            'workflow.looptriggerflow.loop_tag_description',
            'Current loop item injected by a Loop Trigger Flow replay.'
          )
        }))
    ];
  }, [tags, currentSettingForDialog, fallbackFlowId, settings, t]);

  const isSelectedFlowMain = useMemo(() => {
    if (!selectedFlow) {
      return false;
    }
    if (Number(selectedFlow.is_main) === 1) {
      return true;
    }
    const normalizedName = String(selectedFlow.name || '').trim().toLowerCase();
    return normalizedName === 'main flow';
  }, [selectedFlow]);

  const selectedFlowTriggerMode = useMemo(() => {
    if (isSelectedFlowMain) {
      return 'event';
    }
    return normalizeFlowTriggerMode(selectedFlow?.trigger_mode, selectedFlow?.auto_run_enabled);
  }, [selectedFlow, isSelectedFlowMain]);

  const selectedFlowTriggerModeLabel = useMemo(() => {
    if (!selectedFlow) {
      return '';
    }
    if (selectedFlowTriggerMode === 'disabled') {
      return t('workflow.automation_mode_disabled', 'Disabled');
    }
    if (selectedFlowTriggerMode === 'event') {
      return t('workflow.automation_trigger_on_event', 'On update events');
    }
    const frequencyValue = Math.max(1, Number(selectedFlow.auto_run_frequency_value || 1));
    const unit = normalizeFlowAutomationUnit(selectedFlow.auto_run_frequency_unit);
    const unitLabelMap = {
      minutes: t('workflow.automation_unit_minutes', 'minutes'),
      hours: t('workflow.automation_unit_hours', 'hours'),
      days: t('workflow.automation_unit_days', 'days')
    };
    return t('workflow.automation_every', 'Auto every {{value}} {{unit}}', {
      value: frequencyValue,
      unit: unitLabelMap[unit] || unit
    });
  }, [selectedFlow, selectedFlowTriggerMode, t]);

  const selectedFlowTriggerVisual = useMemo(
    () => getTriggerVisualConfig(selectedFlowTriggerMode),
    [selectedFlowTriggerMode]
  );
  const TriggerModeIcon = selectedFlowTriggerVisual.icon;

  const showFlowTriggerNode = useMemo(
    () => Boolean(selectedFlow),
    [selectedFlow]
  );

  useEffect(() => {
    if (ws && caseid) {
      setTags([]);
      setHasRequestedTags(false);
      setIsLoading(true);
      setFlows([]);
      setSelectedFlowId(null);
      ws.send(JSON.stringify({ command: 'getActionPlugins', caseid }));
      ws.send(JSON.stringify({ command: 'getListExportsSettings', caseid }));
      ws.send(JSON.stringify({ command: 'getWorkflowFlows', caseid }));
    }
  }, [ws, caseid]);

  // Load tags lazily only when action settings dialog is opened.
  useEffect(() => {
    if (!ws || !caseid || !isSettingDialogOpen || hasRequestedTags) {
      return;
    }
    ws.send(JSON.stringify({ command: 'get_all_unique_tags', caseid }));
    setHasRequestedTags(true);
  }, [ws, caseid, isSettingDialogOpen, hasRequestedTags]);

  // Fallback: clear loading after a reasonable time if server doesn't reply
  useEffect(() => {
    if (!isLoading) return undefined;
    const fallback = setTimeout(() => {
      setIsLoading(false);
    }, 12000);
    return () => clearTimeout(fallback);
  }, [isLoading]);

  // Auto-open action dialog after OAuth redirect
  useEffect(() => {
    if (autoOpenExportId && settings.length > 0 && !isSettingDialogOpen) {
      const setting = settings.find(s => String(s.id) === String(autoOpenExportId));
      if (setting) {
        setSelectedSettingId(setting.id);
        setSelectedSettingType(setting.type);
        if (setting.flow_id !== undefined && setting.flow_id !== null) {
          setSelectedFlowId(setting.flow_id);
        }
        setIsSettingDialogOpen(true);
        if (onExportOpened) {
          onExportOpened();
        }
      }
    }
  }, [autoOpenExportId, settings, isSettingDialogOpen, onExportOpened]);
  // Optimized WebSocket message handlers
  const handleGetListExportSettings = useCallback((parsedMessage) => {
    const serverSettingsArray = parsedMessage.data || [];
    if (!Array.isArray(serverSettingsArray)) {
      console.error('getListExportSettings: data is not an array', parsedMessage.data);
      return;
    }

    const filteredSettingsFromServer = filterSupportedSettings(serverSettingsArray, pluginTypeSet);
    setSettings(prevSettings => {
      const localSettingsMap = new Map(prevSettings.map((setting) => [setting.id, setting]));
      const resultSettings = filteredSettingsFromServer.map((serverSetting) => {
        const localSetting = localSettingsMap.get(serverSetting.id);
        if (localSetting && localSetting.id === selectedSettingId && isSettingDialogOpen) {
          return { ...serverSetting, ...localSetting };
        }
        return serverSetting;
      });

      prevSettings.forEach((localSetting) => {
        if (typeof localSetting.id === 'string' && localSetting.id.startsWith('temp_')) {
          resultSettings.push(localSetting);
        }
      });

      // Update running workflows based on enabled settings
      setRunningWorkflows(prev => {
        const newRunning = new Set();
        resultSettings.forEach(setting => {
          if (isActionEnabled(setting.enable)) {
            newRunning.add(setting.id);
          }
        });
        return newRunning;
      });

      return resultSettings;
    });

    // Clear loading state once data is received
    setIsLoading(false);
  }, [selectedSettingId, isSettingDialogOpen, pluginTypeSet]);

  const handleSaveActionSettings = useCallback((parsedMessage) => {
    const savedSetting = parsedMessage.data;
    const settingType = savedSetting.type;

    setSettings(prev => {
      const tempSelectedSetting = prev.find(s => typeof s.id === 'string' && s.id.startsWith('temp_') && selectedSettingId === s.id);
      const updatedSettings = prev.filter(s => {
        if (typeof s.id === 'string' && s.id.startsWith('temp_') && selectedSettingId === s.id) {
          return false;
        }
        return true;
      });

      const existingIndex = updatedSettings.findIndex(s => s.id === savedSetting.id);
      const existingSetting = existingIndex >= 0 ? updatedSettings[existingIndex] : null;
      const resolvedScope = savedSetting.scope || existingSetting?.scope || tempSelectedSetting?.scope;

      if (existingIndex >= 0) {
        updatedSettings[existingIndex] = {
          ...savedSetting,
          scope: resolvedScope,
          name: savedSetting.name || updatedSettings[existingIndex].name, // Preserve existing name if not provided
          data: {
            ...updatedSettings[existingIndex].data,
            ...savedSetting.customData
          }
        };
      } else {
        const newSetting = {
          ...savedSetting,
          scope: resolvedScope,
          name: savedSetting.name || `New ${savedSetting.type || 'Flow'}`, // Provide a default name if not provided
          data: savedSetting.customData || {}
        };
        updatedSettings.push(newSetting);
      }
      return updatedSettings;
    });

    setSelectedSettingId(savedSetting.id);
    setSelectedSettingType(settingType);
  }, [selectedSettingId]);

  const sendTemplateAwareCommand = useCallback((payload) => {
    if (!ws || ws.readyState !== WebSocket.OPEN || !payload?.command) {
      return false;
    }

    if (TEMPLATE_GUARDED_COMMANDS.has(payload.command)) {
      setPendingTemplateMutation(payload);
    }

    ws.send(JSON.stringify(payload));
    return true;
  }, [ws]);

  const handleTemplateChangeDecisionRequired = useCallback((parsedMessage) => {
    setTemplateDecisionData(parsedMessage?.data || {});
    setTemplateDecisionDialogOpen(true);
  }, []);

  const handleTemplatePropagationJobStatus = useCallback((parsedMessage) => {
    const payload = parsedMessage?.data || {};
    const job = payload.job || {};
    const totalCases = job.total_cases ?? 0;
    const isLazyMode = job.mode === 'lazy_template_managed';

    if (payload.status === 'queued') {
      setSnackbar({
        open: true,
        message: t('template.propagation_started', `Template propagation started for ${totalCases} linked projects.`),
        severity: 'info'
      });
      return;
    }

    if (payload.status === 'completed') {
      setSnackbar({
        open: true,
        message: t(
          isLazyMode ? 'template.propagation_marked_stale' : 'template.propagation_completed',
          isLazyMode
            ? `Template updated. ${job.success_cases ?? 0} linked projects will refresh actions, flows, and widgets automatically on next open or run.`
            : `Template propagation completed. Updated ${job.success_cases ?? 0} projects, failed ${job.failed_cases ?? 0}.`
        ),
        severity: (job.failed_cases ?? 0) > 0 ? 'warning' : 'success'
      });
      return;
    }

    if (payload.status === 'failed') {
      setSnackbar({
        open: true,
        message: payload.message || t('template.propagation_failed', 'Template propagation failed.'),
        severity: 'error'
      });
    }
  }, [t]);

  const handleTemplateDetachSuccess = useCallback(() => {
    setSnackbar({
      open: true,
      message: t('template.project_detached', 'Project detached from template.'),
      severity: 'info'
    });
  }, [t]);

  const handleCloseTemplateDecisionDialog = useCallback(() => {
    setTemplateDecisionDialogOpen(false);
    setTemplateDecisionData(null);
  }, []);

  const handleCancelTemplateDecision = useCallback(() => {
    handleCloseTemplateDecisionDialog();
    if (ws && ws.readyState === WebSocket.OPEN && caseid) {
      ws.send(JSON.stringify({ command: 'getListExportsSettings', caseid }));
      ws.send(JSON.stringify({ command: 'getWorkflowFlows', caseid }));
    }
  }, [handleCloseTemplateDecisionDialog, ws, caseid]);

  const handleTemplateDecision = useCallback((mode) => {
    if (!pendingTemplateMutation) {
      handleCloseTemplateDecisionDialog();
      return;
    }

    sendTemplateAwareCommand({
      ...pendingTemplateMutation,
      template_change_mode: mode
    });
    handleCloseTemplateDecisionDialog();
  }, [pendingTemplateMutation, sendTemplateAwareCommand, handleCloseTemplateDecisionDialog]);

  const handleCloseSnackbar = useCallback(() => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  }, []);

  const handleToggleFlowEnableResponse = useCallback((parsedMessage) => {
    const responseData = parsedMessage.data;
    const success = responseData.success;

    if (success && responseData) {
      const { id, enable } = responseData;
      console.log(`Flow ${id} ${enable ? 'enabled' : 'disabled'} successfully`);

      // Update settings state
      setSettings(prevSettings =>
        prevSettings.map(s =>
          s.id === id ? { ...s, enable } : s
        )
      );

      // Update running workflows state
      setRunningWorkflows(prev => {
        const newSet = new Set(prev);
        if (enable === 1) {
          newSet.add(id);
        } else {
          newSet.delete(id);
        }
        return newSet;
      });
    } else {
      console.error(`Failed to toggle flow ${responseData?.id || 'unknown'}`, responseData);
      if (responseData?.id) {
        // Revert the optimistic update on failure
        setSettings(prevSettings =>
          prevSettings.map(s =>
            s.id === responseData.id ? { ...s, enable: responseData.enable === 1 ? 0 : 1 } : s
          )
        );

        // Revert running workflows state on failure
        setRunningWorkflows(prev => {
          const newSet = new Set(prev);
          const revertedEnable = responseData.enable === 1 ? 0 : 1;
          if (revertedEnable === 1) {
            newSet.add(responseData.id);
          } else {
            newSet.delete(responseData.id);
          }
          return newSet;
        });
      }
    }
  }, []);
  const handleGetAllUniqueTags = useCallback((parsedMessage) => {
    const receivedTags = parsedMessage.data.tags || [];
    console.log('Received tags:', receivedTags);
    setTags(receivedTags);
  }, []);

  const handleGetActionPlugins = useCallback((parsedMessage) => {
    const plugins = Array.isArray(parsedMessage.data) ? parsedMessage.data : [];
    setPluginActions(plugins.filter((plugin) => plugin && typeof plugin.type === 'string'));
    // Refresh list once plugin metadata is loaded to ensure plugin actions are not filtered out.
    if (ws && ws.readyState === WebSocket.OPEN && caseid) {
      ws.send(JSON.stringify({ command: 'getListExportsSettings', caseid }));
    }
  }, [ws, caseid]);

  const handleGetWorkflowFlows = useCallback((parsedMessage) => {
    const payload = parsedMessage?.data || {};
    const flowList = Array.isArray(payload.flows) ? payload.flows : [];
    setFlows(flowList);
    setSelectedFlowId((prev) => {
      if (flowList.length === 0) {
        return null;
      }
      if (prev !== null && flowList.some((flow) => String(flow.id) === String(prev))) {
        return prev;
      }
      return flowList[0].id;
    });
  }, []);

  const handleCreateWorkflowFlowResponse = useCallback((parsedMessage) => {
    const data = parsedMessage?.data || {};
    if (data.status === 'success' && data.flow?.id !== undefined) {
      setSelectedFlowId(data.flow.id);
    } else if (data.status === 'error') {
      console.error(`Failed to create flow: ${data.message || 'Unknown error'}`);
    }
  }, []);

  const handleRenameWorkflowFlowResponse = useCallback((parsedMessage) => {
    const data = parsedMessage?.data || {};
    if (data.status !== 'success') {
      console.error(`Failed to rename flow: ${data.message || 'Unknown error'}`);
      return;
    }
    const flowPayload = data.flow || {};
    setFlows((prev) => prev.map((flow) => (
      String(flow.id) === String(flowPayload.id)
        ? { ...flow, name: flowPayload.name || flow.name }
        : flow
    )));
  }, []);

  const handleDeleteWorkflowFlowResponse = useCallback((parsedMessage) => {
    const data = parsedMessage?.data || {};
    if (data.status !== 'success') {
      console.error(`Failed to delete flow: ${data.message || 'Unknown error'}`);
      return;
    }
    if (data.replacement_flow_id !== undefined && data.replacement_flow_id !== null) {
      setSelectedFlowId(data.replacement_flow_id);
    }
  }, []);

  const handleUpdateWorkflowFlowAutomationResponse = useCallback((parsedMessage) => {
    const data = parsedMessage?.data || {};
    if (data.status !== 'success') {
      const errorMessage = data.message || 'Unknown error';
      console.error(`Failed to update flow automation: ${errorMessage}`);
      window.alert(errorMessage);
    }
  }, []);

  const handleWorkflowFlowOperationResponse = useCallback(() => {
    // No-op handler to keep console noise down for flow-level operations.
  }, []);

  const handleFlowRename = useCallback((parsedMessage) => {
    const responseData = parsedMessage.data;

    if (responseData && responseData.status === "success") {
      const { id, name } = responseData;
      console.log(`Flow ${id} renamed to '${name}' successfully`);
      setSettings(prevSettings =>
        prevSettings.map(s =>
          s.id === id ? { ...s, name } : s
        )
      );
    } else {
      console.error(`Failed to rename flow: ${responseData?.message || 'Unknown error'}`);
      // Optionally show an error message to the user
    }
  }, []);

  const handleToggleActionScopeResponse = useCallback((parsedMessage) => {
    const responseData = parsedMessage.data;

    // Clear loading state
    setTogglingScope(null);

    if (responseData && responseData.success) {
      // Update UI only on successful response from server
      setSettings(prevSettings =>
        prevSettings.map(s =>
          s.id === responseData.id ? { ...s, scope: responseData.scope } : s
        )
      );
      console.log(`Action ${responseData.id} scope updated to ${responseData.scope}`);
    } else {
      console.error('Error toggling action scope:', responseData?.message);
      // Show error notification if needed
      alert(`Failed to toggle scope: ${responseData?.message || 'Unknown error'}`);
    }
  }, []);

  const handleStepProgress = useCallback((parsedMessage) => {
    console.log('handleStepProgress received:', parsedMessage);
    const progress = parsedMessage.data || {};
    console.log('Progress data:', progress);

    if (progress.status === 'processing') {
      console.log('Setting current processing step:', progress.step_id);
      setCurrentProcessingStep(progress.step_id);
      setStartedSteps(prev => new Set([...prev, progress.step_id]));
    } else if (progress.status === 'completed') {
      console.log('Adding to processed steps:', progress.step_id);
      setProcessedSteps(prev => new Set([...prev, progress.step_id]));
      // Don't clear currentProcessingStep here - let the next 'processing' step handle it
      // setCurrentProcessingStep(null);
    }
  }, []);

  const handleForceRun = useCallback((parsedMessage) => {
    // This handler prevents the "No handler found for command: forceRun" console message
    // Individual action components handle their own forceRun responses
    console.log('forceRun command acknowledged in ActionsDataflowList:', parsedMessage);
  }, []);

  const handleReplayAllWorkflowsResponse = useCallback((parsedMessage) => {
    const responseData = parsedMessage.data || {};

    // Handle step progress updates
    if (responseData.step_progress) {
      const { current_step, step_id, step_name, status, total_steps } = responseData.step_progress;

      if (status === 'processing') {
        setCurrentProcessingStep({ id: step_id, name: step_name, index: current_step });
      } else if (status === 'completed') {
        setProcessedSteps(prev => new Set([...prev, step_id]));
        setCurrentProcessingStep(null);
      } else if (status === 'failed') {
        // Handle failed step - mark as processed but keep error indication
        const errorMessage = responseData.step_progress.error || 'Unknown error';
        console.error(`Step ${step_id} (${step_name}) failed: ${errorMessage}`);
        setProcessedSteps(prev => new Set([...prev, step_id]));
        setCurrentProcessingStep(null);

        // Show error notification
        setWorkflowError({
          stepId: step_id,
          stepName: step_name,
          stepIndex: current_step,
          totalSteps: total_steps,
          errorMessage: errorMessage
        });
      }

      return; // Don't reset state for progress updates
    }

    // Clear timeout if it exists
    if (replayTimeoutId) {
      clearTimeout(replayTimeoutId);
      setReplayTimeoutId(null);
    }

    // Reset processing state
    setIsReplayingAllWorkflows(false);
    setCurrentProcessingStep(null);

    if (responseData && responseData.status === "success") {
      console.log(`Successfully replayed all workflows: ${responseData.message}`);
      // Clear the visual states after a delay to show completion
      setTimeout(() => {
        setProcessedSteps(new Set());
        setStartedSteps(new Set());
      }, 2000);
    } else if (responseData && responseData.status === "error") {
      // Handle explicit error response
      console.error(`Failed to replay all workflows: ${responseData?.message || 'Unknown error'}`);
      // Clear states immediately on error
      setProcessedSteps(new Set());
      setStartedSteps(new Set());

      // Show error notification
      setWorkflowError({
        stepId: responseData.stopped_at_workflow || 'unknown',
        stepName: 'Workflow Processing',
        stepIndex: responseData.workflows_count || 0,
        totalSteps: responseData.workflows_count || 0,
        errorMessage: responseData.message || responseData.error || 'Unknown error'
      });
    } else {
      // Handle unexpected response format or missing status
      console.error(`Unexpected response from replay all workflows:`, responseData);
      // Clear states immediately on unexpected response
      setProcessedSteps(new Set());
      setStartedSteps(new Set());
    }
  }, [replayTimeoutId]);

  // Optimized WebSocket message routing
  const messageHandlers = useMemo(() => ({
    'getActionPlugins': handleGetActionPlugins,
    'getListExportSettings': handleGetListExportSettings,
    'getWorkflowFlowsResponse': handleGetWorkflowFlows,
    'saveActionSettings': handleSaveActionSettings,
    'templateChangeDecisionRequired': handleTemplateChangeDecisionRequired,
    'templatePropagationJobStatus': handleTemplatePropagationJobStatus,
    'templateProjectDetached': handleTemplateDetachSuccess,
    'get_all_unique_tags': handleGetAllUniqueTags,
    'toggleFlowEnableResponse': handleToggleFlowEnableResponse,
    'toggleActionScope': handleToggleActionScopeResponse,
    'flowRename': handleFlowRename,
    'createWorkflowFlowResponse': handleCreateWorkflowFlowResponse,
    'renameWorkflowFlowResponse': handleRenameWorkflowFlowResponse,
    'deleteWorkflowFlowResponse': handleDeleteWorkflowFlowResponse,
    'updateWorkflowFlowAutomationResponse': handleUpdateWorkflowFlowAutomationResponse,
    'swapWorkflowFlowOrderResponse': handleWorkflowFlowOperationResponse,
    'moveActionToFlowResponse': handleWorkflowFlowOperationResponse,
    'forceRun': handleForceRun,
    'replayAllWorkflows': handleReplayAllWorkflowsResponse,
    'stepProgress': handleStepProgress
  }), [
    handleGetActionPlugins,
    handleGetListExportSettings,
    handleGetWorkflowFlows,
    handleSaveActionSettings,
    handleTemplateChangeDecisionRequired,
    handleTemplatePropagationJobStatus,
    handleTemplateDetachSuccess,
    handleGetAllUniqueTags,
    handleToggleFlowEnableResponse,
    handleToggleActionScopeResponse,
    handleFlowRename,
    handleCreateWorkflowFlowResponse,
    handleRenameWorkflowFlowResponse,
    handleDeleteWorkflowFlowResponse,
    handleUpdateWorkflowFlowAutomationResponse,
    handleWorkflowFlowOperationResponse,
    handleForceRun,
    handleReplayAllWorkflowsResponse,
    handleStepProgress
  ]);

  useEffect(() => {
    const unsubscribe = subscribeToMessages((parsedMessage) => {
      try {
        console.log('WebSocket message received in ActionsDataflowList:', parsedMessage);
        const handler = messageHandlers[parsedMessage.command];
        if (handler) {
          console.log('Found handler for command:', parsedMessage.command);
          handler(parsedMessage);
        } else {
          console.log('No handler found for command:', parsedMessage.command);
        }
      } catch (error) {
        console.error('Error parsing WebSocket message in ActionsDataflowList:', error);
      }
    });
    return unsubscribe;
  }, [subscribeToMessages, messageHandlers]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (replayTimeoutId) {
        clearTimeout(replayTimeoutId);
      }
    };
  }, [replayTimeoutId]);

  const handleSettingSaveStatusChange = useCallback((itemId, isDirty, nameOrSettingObject, oldItemIdIfChanged) => {
    setSettings(prevSettings => {
      // Check if this is a new setting (temp ID that doesn't exist in the list)
      const existingSettingIndex = prevSettings.findIndex(s => s.id === (oldItemIdIfChanged || itemId));

      if (existingSettingIndex === -1) {
        // This is a new setting - add it to the list
        if (typeof nameOrSettingObject === 'object' && nameOrSettingObject !== null) {
          const newSetting = {
            id: itemId,
            type: selectedSettingType,
            enable: 0,
            caseid: caseid,
            interval: '0',
            name: nameOrSettingObject.name || pluginNameByType[selectedSettingType] || t(`settings.new_${selectedSettingType}_name`, `New ${selectedSettingType.charAt(0).toUpperCase() + selectedSettingType.slice(1)}`),
            flow_id: selectedFlowId ?? (flows[0]?.id ?? null),
            data: nameOrSettingObject.data || {},
            ...nameOrSettingObject
          };
          return [...prevSettings, newSetting];
        }
        return prevSettings;
      }

      // Existing setting - update it
      return prevSettings.map(s => {
        if (s.id === (oldItemIdIfChanged || itemId)) {
          const currentLocalSetting = s;
          let newNameString = currentLocalSetting.name;
          let updatesFromObject = {};

          if (typeof nameOrSettingObject === 'string') {
            newNameString = nameOrSettingObject;
          } else if (typeof nameOrSettingObject === 'object' && nameOrSettingObject !== null) {
            updatesFromObject = { ...nameOrSettingObject };
            if (nameOrSettingObject.name !== undefined && typeof nameOrSettingObject.name === 'string') {
              newNameString = nameOrSettingObject.name;
            } else if (nameOrSettingObject.name !== undefined && typeof nameOrSettingObject.name !== 'string') {
              console.warn('Setting item received an object with a non-string name property:', nameOrSettingObject);
            }
          }

          const updatedSetting = {
            ...currentLocalSetting,
            ...updatesFromObject,
            id: itemId,
            name: newNameString
          };

          return updatedSetting;
        }
        return s;
      });
    });

    if (oldItemIdIfChanged) {
      setSelectedSettingId(itemId); // Update selection to the new permanent ID
    }

    // If the item was saved (not dirty) and the dialog is open for this item, close it.
    // Only close if a temporary item was saved and became permanent (oldItemIdIfChanged is true).
    // For existing items, saving them will make them !isDirty, but the dialog will remain open.
    if (!isDirty && isSettingDialogOpen && (selectedSettingId === itemId || selectedSettingId === oldItemIdIfChanged)) {
      if (oldItemIdIfChanged) {
        setIsSettingDialogOpen(false);
      }
    }
  }, [selectedSettingType, caseid, t, selectedSettingId, isSettingDialogOpen, pluginNameByType, selectedFlowId, flows]);
  // Event handlers with useCallback optimization
  const handleToggleEnable = useCallback((settingId, currentEnableState) => {
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      console.warn("WebSocket not available for toggle enable/disable");
      return;
    }

    const newEnableState = currentEnableState === 1 ? 0 : 1;

    console.log(`Toggling flow ${settingId} from ${currentEnableState} to ${newEnableState}`);
    sendTemplateAwareCommand({
      command: 'toggleFlowEnable',
      id: settingId,
      enable: newEnableState,
      caseid: caseid
    });

    // Optimistic update
    setSettings(prevSettings => {
      const updatedSettings = prevSettings.map(s =>
        s.id === settingId ? { ...s, enable: newEnableState } : s
      );
      return updatedSettings;
    });

    // Track running workflows
    setRunningWorkflows(prev => {
      const newSet = new Set(prev);
      if (newEnableState === 1) {
        newSet.add(settingId);
      } else {
        newSet.delete(settingId);
      }
      return newSet;
    });
  }, [ws, caseid]);

  const handleToggleScope = useCallback((settingId, currentScope) => {
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      console.warn("WebSocket not available for toggle scope");
      return;
    }

    // Show loading state
    setTogglingScope(settingId);

    const newScope = currentScope === 'project' ? 'event' : 'project';

    console.log(`Toggling scope for action ${settingId} from ${currentScope} to ${newScope}`);
    sendTemplateAwareCommand({
      command: 'toggleActionScope',
      id: settingId,
      scope: newScope,
      caseid: caseid
    });

    // No optimistic update - wait for server response
  }, [ws, caseid]);

  // Handler for direct scope change (used in dialog)
  const handleScopeChange = useCallback((settingId, newScope) => {
    // For new/temp settings, update local state only
    const isNewSetting = typeof settingId === 'string' && settingId.startsWith('temp_');

    if (isNewSetting) {
      // Update local state for new settings
      setSettings(prevSettings =>
        prevSettings.map(s =>
          s.id === settingId ? { ...s, scope: newScope } : s
        )
      );
      console.log(`Updated local scope for new action ${settingId} to ${newScope}`);
      return;
    }

    // For existing settings, send to server
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      console.warn("WebSocket not available for scope change");
      return;
    }

    // Show loading state
    setTogglingScope(settingId);

    console.log(`Setting scope for action ${settingId} to ${newScope}`);
    sendTemplateAwareCommand({
      command: 'toggleActionScope',
      id: settingId,
      scope: newScope,
      caseid: caseid
    });

    // No optimistic update - wait for server response
  }, [ws, caseid]);

  // Menu handlers for AddActionMenu component
  const handleOpenMenu = useCallback(() => setAnchorEl(true), []);
  const handleCloseMenu = useCallback(() => setAnchorEl(false), []);
  const handleAddNewSetting = useCallback((type, scope = 'event') => {
    const targetFlowId = selectedFlowId ?? (sortedFlows[0]?.id ?? null);

    // We allow adding new settings even if others are unsaved/disabled
    // The backend will handle the persistence immediately

    const tempId = `temp_${Date.now()}`;
    // Don't add to settings list immediately - just set up for dialog
    setSelectedSettingId(tempId);
    setSelectedSettingType(type);

    // Store the initial scope for new settings in the temp setting object for immediate UI feedback
    setSettings(prevSettings => [...prevSettings, {
      id: tempId,
      type: type,
      enable: 0,
      caseid: caseid,
      interval: '0',
      name: '',
      scope: scope,
      flow_id: targetFlowId,
      data: {}
    }]);

    setIsSettingDialogOpen(true);

    // Immediately persist the new action to the backend
    if (ws && ws.readyState === WebSocket.OPEN) {
      sendTemplateAwareCommand({
        command: 'saveActionSettings',
        id: 0, // 0 indicates new record to backend
        caseid: caseid,
        type: type,
        enable: '0', // Disabled by default
        interval: 0,
        data: {},
        scope: scope,
        flow_id: targetFlowId
      });
    }
  }, [caseid, ws, selectedFlowId, sortedFlows]);

  const handleMenuItemSelect = useCallback((actionType, scope = 'event') => {
    handleCloseMenu();
    handleAddNewSetting(actionType, scope);
  }, [handleCloseMenu, handleAddNewSetting]);

  const handleDeleteConfirm = useCallback((settingId) => {
    setIdToDelete(settingId);
    setDeleteConfirmOpen(true);
  }, []);

  const handleDeleteCancel = useCallback(() => {
    setDeleteConfirmOpen(false);
    setIdToDelete(null);
  }, []);

  const handleDeleteExecute = useCallback(() => {
    if (!idToDelete) return;
    const settingToDelete = settings.find(setting => setting.id === idToDelete);
    if (!settingToDelete) return;

    if (ws && ws.readyState === WebSocket.OPEN) {
      sendTemplateAwareCommand({
        command: 'deleteExportSettings',
        id: idToDelete,
        type: settingToDelete.type,
        caseid
      });
    }

    const updatedSettings = settings.filter(setting => setting.id !== idToDelete);
    setSettings(updatedSettings);

    if (selectedSettingId === idToDelete) {
      if (updatedSettings.length > 0) {
        setSelectedSettingId(updatedSettings[0].id);
        setSelectedSettingType(updatedSettings[0].type);
      } else {
        setSelectedSettingId(null);
        setSelectedSettingType(null);
      }
    }
    setDeleteConfirmOpen(false);
    setIdToDelete(null);
  }, [idToDelete, settings, ws, caseid, selectedSettingId]);

  const handleNameSave = useCallback((settingId, newName) => {
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      console.warn("WebSocket not available for name save");
      return;
    }

    const setting = settings.find(s => s.id === settingId);
    if (!setting) return;

    sendTemplateAwareCommand({
      command: 'flowRename',
      id: settingId,
      name: newName
    });

    setSettings(prevSettings =>
      prevSettings.map(s =>
        s.id === settingId ? { ...s, name: newName } : s
      )
    );
  }, [ws, settings]);

  const handleSettingDialogClose = useCallback(() => {
    // Just close the dialog and reset selection - no need to remove from list since we never added it
    setIsSettingDialogOpen(false);
    setSelectedSettingId(null);
    setSelectedSettingType(null);
  }, []);

  const handleMoveSetting = useCallback((settingId, direction) => {
    setSettings(currentSettings => {
      const index = currentSettings.findIndex(s => s.id === settingId);
      if (index === -1) return currentSettings;

      const newSettings = [...currentSettings];
      const settingName = newSettings[index].name || newSettings[index].type;
      const currentFlowId = resolveSettingFlowId(newSettings[index]);

      let id1 = settingId;
      let id2;

      if (direction === 'left' && index > 0 && String(resolveSettingFlowId(newSettings[index - 1])) === String(currentFlowId)) {
        id2 = newSettings[index - 1].id;
        [newSettings[index - 1], newSettings[index]] = [newSettings[index], newSettings[index - 1]];
        console.log(`Moved setting '${settingName}' (ID: ${settingId}) LEFT. New order:`, newSettings.map(s => ({ id: s.id, name: s.name || s.type })));
        if (ws && ws.readyState === WebSocket.OPEN) {
          sendTemplateAwareCommand({
            command: 'swapfloworder',
            id1: id1,
            id2: id2,
            caseid: caseid
          });
        }
        return newSettings;
      } else if (direction === 'right' && index < newSettings.length - 1 && String(resolveSettingFlowId(newSettings[index + 1])) === String(currentFlowId)) {
        id2 = newSettings[index + 1].id;
        [newSettings[index + 1], newSettings[index]] = [newSettings[index], newSettings[index + 1]];
        console.log(`Moved setting '${settingName}' (ID: ${settingId}) RIGHT. New order:`, newSettings.map(s => ({ id: s.id, name: s.name || s.type })));
        if (ws && ws.readyState === WebSocket.OPEN) {
          sendTemplateAwareCommand({
            command: 'swapfloworder',
            id1: id1,
            id2: id2,
            caseid: caseid
          });
        }
        return newSettings;
      }
      return currentSettings;
    });
  }, [ws, caseid, resolveSettingFlowId, sendTemplateAwareCommand]);

  const handleReplayConfirmOpen = useCallback(() => {
    if (isReplayingAllWorkflows) return;
    setReplayConfirmOpen(true);
  }, [isReplayingAllWorkflows]);

  const handleReplayConfirmClose = useCallback(() => {
    setReplayConfirmOpen(false);
  }, []);

  const handleReplayAllWorkflows = useCallback(() => {
    setReplayConfirmOpen(false); // Close dialog

    if (!ws || ws.readyState !== WebSocket.OPEN) {
      console.warn("WebSocket not available for replay all workflows");
      return;
    }

    if (!caseid) {
      console.warn("No case ID available for replay all workflows");
      return;
    }

    if (isReplayingAllWorkflows) {
      console.warn("Replay all workflows is already in progress");
      return;
    }

    console.log(`Triggering replay of all workflows for case ${caseid}`);

    // Set processing state and reset step tracking
    setIsReplayingAllWorkflows(true);
    setCurrentProcessingStep(null);
    setProcessedSteps(new Set());
    setStartedSteps(new Set());

    // Set a timeout to reset the state if no response is received (30 seconds)
    const timeoutId = setTimeout(() => {
      console.warn("Replay all workflows timed out - resetting state");
      setIsReplayingAllWorkflows(false);
      setCurrentProcessingStep(null);
      setProcessedSteps(new Set());
      setStartedSteps(new Set());
      setReplayTimeoutId(null);
    }, 30000);

    setReplayTimeoutId(timeoutId);

    // Send command to trigger all workflows for this case
    ws.send(JSON.stringify({
      command: 'replayAllWorkflows',
      caseid: caseid
    }));

    // Optionally show a notification or update UI state to indicate the action was triggered
    // You could add a toast notification here if you have a notification system

  }, [ws, caseid, isReplayingAllWorkflows]);

  const openFlowAutomationDialog = useCallback((mode, flow = null) => {
    const normalizedMode = mode === 'edit' ? 'edit' : 'create';
    const isEdit = normalizedMode === 'edit';
    const triggerMode = isEdit
      ? normalizeFlowTriggerMode(flow?.trigger_mode, flow?.auto_run_enabled)
      : 'event';
    const frequencyValue = isEdit && triggerMode === 'time'
      ? Math.max(1, Number(flow?.auto_run_frequency_value || 1))
      : 1;
    const frequencyUnit = normalizeFlowAutomationUnit(
      isEdit ? flow?.auto_run_frequency_unit : 'minutes'
    );

    setFlowAutomationDialogMode(normalizedMode);
    setFlowAutomationDialogFlowId(isEdit ? flow?.id ?? null : null);
    setFlowAutomationDialogName(
      isEdit ? String(flow?.name || '') : t('workflow.new_flow_default', 'New Flow')
    );
    setFlowAutomationDialogTriggerMode(triggerMode);
    setFlowAutomationDialogFrequencyValue(String(frequencyValue));
    setFlowAutomationDialogFrequencyUnit(frequencyUnit);
    setFlowAutomationDialogError('');
    setFlowAutomationDialogOpen(true);
  }, [t]);

  const handleCloseFlowAutomationDialog = useCallback(() => {
    setFlowAutomationDialogOpen(false);
    setFlowAutomationDialogError('');
  }, []);

  const handleSubmitFlowAutomationDialog = useCallback(() => {
    if (!ws || ws.readyState !== WebSocket.OPEN || !caseid) {
      return;
    }

    const triggerMode = normalizeFlowTriggerMode(flowAutomationDialogTriggerMode);
    let frequencyValue = 1;
    if (triggerMode === 'time') {
      frequencyValue = Number(flowAutomationDialogFrequencyValue);
      if (!Number.isInteger(frequencyValue) || frequencyValue <= 0) {
        setFlowAutomationDialogError(
          t('workflow.automation_frequency_value_invalid', 'Frequency must be a number greater than 0.')
        );
        return;
      }
    }

    const frequencyUnit = normalizeFlowAutomationUnit(flowAutomationDialogFrequencyUnit);
    const automationPayload = buildFlowAutomationPayload(triggerMode, frequencyValue, frequencyUnit);

    if (flowAutomationDialogMode === 'create') {
      const flowName = String(flowAutomationDialogName || '').trim();
      if (!flowName) {
        setFlowAutomationDialogError(
          t('workflow.automation_flow_name_required', 'Flow name is required.')
        );
        return;
      }
      sendTemplateAwareCommand({
        command: 'createWorkflowFlow',
        caseid,
        name: flowName,
        ...automationPayload
      });
    } else {
      if (!flowAutomationDialogFlowId) {
        setFlowAutomationDialogError(t('workflow.invalid_flow_id', 'Invalid flow ID.'));
        return;
      }
      sendTemplateAwareCommand({
        command: 'updateWorkflowFlowAutomation',
        flow_id: flowAutomationDialogFlowId,
        ...automationPayload
      });
    }

    handleCloseFlowAutomationDialog();
  }, [
    ws,
    caseid,
    flowAutomationDialogTriggerMode,
    flowAutomationDialogFrequencyValue,
    flowAutomationDialogFrequencyUnit,
    flowAutomationDialogMode,
    flowAutomationDialogName,
    flowAutomationDialogFlowId,
    t,
    handleCloseFlowAutomationDialog,
    sendTemplateAwareCommand
  ]);

  const handleCreateFlow = useCallback(() => {
    if (!ws || ws.readyState !== WebSocket.OPEN || !caseid) {
      return;
    }
    openFlowAutomationDialog('create');
  }, [ws, caseid, openFlowAutomationDialog]);

  const handleRenameSelectedFlow = useCallback(() => {
    if (!ws || ws.readyState !== WebSocket.OPEN || !selectedFlow) {
      return;
    }
    setFlowRenameDialogValue(String(selectedFlow.name || ''));
    setFlowRenameDialogError('');
    setFlowRenameDialogOpen(true);
  }, [ws, selectedFlow]);

  const handleCloseFlowRenameDialog = useCallback(() => {
    setFlowRenameDialogOpen(false);
    setFlowRenameDialogError('');
  }, []);

  const handleSubmitFlowRenameDialog = useCallback(() => {
    if (!ws || ws.readyState !== WebSocket.OPEN || !selectedFlow) {
      return;
    }
    const proposedName = String(flowRenameDialogValue || '').trim();
    if (!proposedName) {
      setFlowRenameDialogError(
        t('workflow.automation_flow_name_required', 'Flow name is required.')
      );
      return;
    }
    sendTemplateAwareCommand({
      command: 'renameWorkflowFlow',
      flow_id: selectedFlow.id,
      name: proposedName
    });
    handleCloseFlowRenameDialog();
  }, [ws, selectedFlow, flowRenameDialogValue, t, handleCloseFlowRenameDialog, sendTemplateAwareCommand]);

  const handleDeleteSelectedFlow = useCallback(() => {
    if (!ws || ws.readyState !== WebSocket.OPEN || !selectedFlow) {
      return;
    }
    if (isSelectedFlowMain) {
      return;
    }
    setFlowDeleteDialogOpen(true);
  }, [ws, selectedFlow, isSelectedFlowMain, sendTemplateAwareCommand]);

  const handleCloseFlowDeleteDialog = useCallback(() => {
    setFlowDeleteDialogOpen(false);
  }, []);

  const handleSubmitFlowDeleteDialog = useCallback(() => {
    if (!ws || ws.readyState !== WebSocket.OPEN || !selectedFlow || isSelectedFlowMain) {
      return;
    }
    sendTemplateAwareCommand({
      command: 'deleteWorkflowFlow',
      flow_id: selectedFlow.id
    });
    setFlowDeleteDialogOpen(false);
  }, [ws, selectedFlow, isSelectedFlowMain]);

  const handleConfigureSelectedFlowAutomation = useCallback(() => {
    if (!ws || ws.readyState !== WebSocket.OPEN || !selectedFlow) {
      return;
    }
    if (isSelectedFlowMain) {
      window.alert(t('workflow.main_flow_automation_forbidden', 'Main Flow automation is disabled.'));
      return;
    }
    openFlowAutomationDialog('edit', selectedFlow);
  }, [ws, selectedFlow, isSelectedFlowMain, t, openFlowAutomationDialog]);

  const handleTriggerNodeClick = useCallback(() => {
    if (!selectedFlow || isSelectedFlowMain) {
      return;
    }
    handleConfigureSelectedFlowAutomation();
  }, [selectedFlow, isSelectedFlowMain, handleConfigureSelectedFlowAutomation]);

  // Memoized style objects for performance
  const styles = useMemo(() => ({
    controlButton: {
      color: '#5757b0',
      width: '24px',
      height: '24px',
      borderRadius: '50%',
      backgroundColor: 'transparent',
      opacity: 1, // Keep control buttons fully opaque even when the action is disabled
      transition: 'all 0.2s ease-in-out',
      '&:hover': {
        backgroundColor: 'rgba(87, 87, 176, 0.1)',
        color: '#4a4a9a',
        transform: 'scale(1.1)',
      },
      fontSize: '0.9rem',
      fontWeight: 'bold'
    },
    deleteButton: {
      color: '#ff4444',
      width: '24px',
      height: '24px',
      borderRadius: '50%',
      backgroundColor: 'transparent',
      opacity: 1, // Keep delete button fully opaque when the action is disabled
      transition: 'all 0.2s ease-in-out',
      '&:hover': {
        backgroundColor: 'rgba(255, 68, 68, 0.1)',
        color: '#cc0000',
        transform: 'scale(1.1)',
      }
    }
  }), []);

  // Get the visual status of each workflow item
  const getItemStatus = useCallback((itemId) => {
    if (processedSteps.has(itemId)) {
      return 'finished';
    } else if (currentProcessingStep === itemId || startedSteps.has(itemId)) {
      return 'started';
    } else {
      return 'pending';
    }
  }, [processedSteps, currentProcessingStep, startedSteps]);

  return (
    <Box>
      <Box sx={{ m: 1 }}>        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <AppTooltip title={t('workflow.tooltip', 'Create automated workflows. Chain multiple actions together to build powerful automation sequences.')}>
            <IconButton size="small" sx={{ mr: 1 }}><InfoIcon fontSize="inherit" /></IconButton>
          </AppTooltip>
          <Typography variant="body2" color="text.secondary">
            {t('workflow.description', 'These actions will be executed automatically after every event is processed.')}
          </Typography>
        </Box>

        {/* Play Button Icon - Top Right */}
        {settings.length > 0 && (
          <AppTooltip
            title={
              <div>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {t('workflow.replay_all_tooltip_en', 'Replay All Workflows')}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                  {t('workflow.replay_all_tooltip_fr', 'Rejouer tous les flux de travail')}
                </Typography>
              </div>
            }
            arrow
            placement="left"
          >
            <IconButton
              onClick={handleReplayConfirmOpen}
              disabled={isReplayingAllWorkflows}
              sx={{
                backgroundColor: isReplayingAllWorkflows ? '#e0e0e0' : '#4caf50',
                color: 'white',
                width: { xs: 36, md: 48 },
                height: { xs: 36, md: 48 },
                borderRadius: '12px',
                boxShadow: isReplayingAllWorkflows
                  ? '0 2px 8px rgba(0,0,0,0.1)'
                  : '0 4px 16px rgba(76, 175, 80, 0.3)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                border: '2px solid transparent',
                position: 'relative',
                overflow: 'hidden',

                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0.05) 100%)',
                  borderRadius: '12px',
                  opacity: 0,
                  transition: 'opacity 0.3s ease',
                },

                '&:hover': {
                  backgroundColor: isReplayingAllWorkflows ? '#e0e0e0' : '#45a049',
                  transform: isReplayingAllWorkflows ? 'none' : 'translateY(-2px) scale(1.05)',
                  boxShadow: isReplayingAllWorkflows
                    ? '0 2px 8px rgba(0,0,0,0.1)'
                    : '0 8px 24px rgba(76, 175, 80, 0.4)',

                  '&::before': {
                    opacity: isReplayingAllWorkflows ? 0 : 1,
                  }
                },

                '&:active': {
                  transform: isReplayingAllWorkflows ? 'none' : 'translateY(0px) scale(0.95)',
                },

                '&.Mui-disabled': {
                  backgroundColor: '#e0e0e0',
                  color: '#9e9e9e',
                }
              }}
            >
              {isReplayingAllWorkflows ? (
                <CircularProgress
                  size={20}
                  sx={{
                    color: '#9e9e9e',
                    animation: 'spin 1s linear infinite',
                    '@keyframes spin': {
                      '0%': { transform: 'rotate(0deg)' },
                      '100%': { transform: 'rotate(360deg)' }
                    },
                    width: { xs: 20, md: 24 },
                    height: { xs: 20, md: 24 }
                  }}
                />
              ) : (
                <PlayArrowIcon
                  sx={{
                    fontSize: { xs: '1.2rem', md: '1.5rem' },
                    filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.2))'
                  }}
                />
              )}
            </IconButton>
          </AppTooltip>
        )}
      </Box>

        <WorkflowFlowHeader
          sortedFlows={sortedFlows}
          fallbackFlowId={fallbackFlowId}
          onSelectFlow={setSelectedFlowId}
          onCreateFlow={handleCreateFlow}
          selectedFlow={selectedFlow}
          onRenameFlow={handleRenameSelectedFlow}
          onDeleteFlow={handleDeleteSelectedFlow}
          canDeleteSelectedFlow={sortedFlows.length > 1 && !isSelectedFlowMain}
          t={t}
        />

        {isLoading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minHeight: '220px', py: 2 }}>
            <Typography variant="body1" sx={{ mb: 1.5, color: 'text.secondary' }}>{t('workflow.loading', 'Loading workflows...')}</Typography>
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap', maxWidth: '900px' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: { xs: '100px', md: '140px' }, pt: '25px', mb: 3 }}>
                <Skeleton variant="circular" animation="wave" sx={{ width: { xs: 60, md: 80 }, height: { xs: 60, md: 80 } }} />
                <Skeleton variant="text" height={24} animation="wave" sx={{ mt: 1, width: { xs: 100, md: 120 } }} />
              </Box>
              <Skeleton variant="rectangular" width={40} height={10} animation="wave" sx={{ borderRadius: 2, width: { xs: 20, md: 40 } }} />
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: { xs: '100px', md: '140px' }, pt: '25px', mb: 3 }}>
                <Skeleton variant="circular" animation="wave" sx={{ width: { xs: 60, md: 80 }, height: { xs: 60, md: 80 } }} />
                <Skeleton variant="text" height={24} animation="wave" sx={{ mt: 1, width: { xs: 100, md: 120 } }} />
              </Box>
              <Skeleton variant="rectangular" width={40} height={10} animation="wave" sx={{ borderRadius: 2, width: { xs: 20, md: 40 } }} />
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: { xs: '100px', md: '140px' }, pt: '25px', mb: 3 }}>
                <Skeleton variant="circular" animation="wave" sx={{ width: { xs: 60, md: 80 }, height: { xs: 60, md: 80 } }} />
                <Skeleton variant="text" height={24} animation="wave" sx={{ mt: 1, width: { xs: 100, md: 120 } }} />
              </Box>
              <Skeleton variant="rectangular" width={40} height={10} animation="wave" sx={{ borderRadius: 2, width: { xs: 20, md: 40 } }} />
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: { xs: '100px', md: '140px' }, pt: '25px', mb: 3 }}>
                <Skeleton variant="circular" animation="wave" sx={{ width: { xs: 60, md: 80 }, height: { xs: 60, md: 80 } }} />
                <Skeleton variant="text" height={24} animation="wave" sx={{ mt: 1, width: { xs: 100, md: 120 } }} />
              </Box>
            </Box>
          </Box>
        ) : (
          <Box sx={{ mb: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center', alignItems: 'flex-start', maxWidth: '900px' }}>
              {showFlowTriggerNode && (
                <Box sx={WORKFLOW_NODE_CONTAINER_SX}>
                  <AppTooltip
                    title={
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {t('workflow.automation_trigger_node_label', 'Flow Trigger')}
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.8 }}>
                          {isSelectedFlowMain
                            ? t('workflow.automation_trigger_on_event', 'On event change')
                            : t('workflow.automation_trigger_click_config', 'Click to configure automation')}
                        </Typography>
                      </Box>
                    }
                    placement="top"
                    arrow
                  >
                    <Box
                      onClick={handleTriggerNodeClick}
                      sx={{
                        color: selectedFlowTriggerVisual.iconColor,
                        border: `3px solid ${selectedFlowTriggerVisual.borderColor}`,
                        background: selectedFlowTriggerVisual.background,
                        borderRadius: '50%',
                        padding: '0px',
                        width: { xs: '60px', md: '80px' },
                        height: { xs: '60px', md: '80px' },
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: isSelectedFlowMain ? 'default' : 'pointer',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        position: 'relative',
                        boxShadow: selectedFlowTriggerVisual.shadow,
                        opacity: selectedFlowTriggerMode === 'disabled' ? 0.82 : isSelectedFlowMain ? 0.92 : 1,
                        filter: selectedFlowTriggerMode === 'disabled' ? 'saturate(0.45) brightness(0.95)' : 'none',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, rgba(255,255,255,0.6) 0%, rgba(255,255,255,0.04) 100%)',
                          pointerEvents: 'none'
                        },
                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          bottom: '-10px',
                          left: '50%',
                          transform: 'translateX(-50%) rotate(45deg)',
                          width: '10px',
                          height: '10px',
                          border: '1px solid #dddddd',
                          borderWidth: '0 1px 1px 0',
                          backgroundColor: '#ffffff',
                          display: 'block'
                        },
                        '&:hover': isSelectedFlowMain ? {} : {
                          transform: 'translateY(-3px) scale(1.02)',
                          boxShadow: selectedFlowTriggerVisual.hoverShadow,
                        }
                      }}
                    >
                      <Box sx={{
                        width: { xs: 38, md: 52 },
                        height: { xs: 38, md: 52 },
                        borderRadius: '50%',
                        background: selectedFlowTriggerVisual.iconBackground,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: 'inset 0 1px 2px rgba(255,255,255,0.55), 0 2px 8px rgba(0,0,0,0.08)',
                        position: 'relative',
                        zIndex: 1
                      }}>
                        <TriggerModeIcon sx={{ fontSize: { xs: 24, md: 34 }, color: selectedFlowTriggerVisual.iconColor }} />
                      </Box>
                    </Box>
                  </AppTooltip>
                  <Box sx={{
                    mt: 1.5,
                    textAlign: 'center',
                    minHeight: '32px',
                    maxHeight: '44px',
                    maxWidth: { xs: '100px', md: '120px' },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden'
                  }}>
                    <Typography sx={{
                      fontSize: { xs: '10px', md: '12px' },
                      fontWeight: 500,
                      textAlign: 'center',
                      color: selectedFlowTriggerVisual.labelColor,
                      width: '100%',
                      overflow: 'hidden',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      lineHeight: '1.2',
                      padding: '4px 6px',
                      borderRadius: '6px',
                      background: selectedFlowTriggerVisual.labelBackground,
                      border: `1px solid ${selectedFlowTriggerVisual.labelBorder}`
                    }}>
                      {selectedFlowTriggerModeLabel}
                    </Typography>
                  </Box>
                </Box>
              )}

              {showFlowTriggerNode && selectedFlowSettings.length > 0 && (
                <WorkflowConnector />
              )}

              {selectedFlowSettings.map((setting, index) => {
                const isEnabled = setting.isEnabled;
                const status = getItemStatus(setting.id);

                return (
                  <React.Fragment key={setting.id}>
                    {index > 0 && (
                      <WorkflowConnector />
                    )}

                    <WorkflowActionNode
                      setting={setting}
                      index={index}
                      totalSettings={selectedFlowSettings.length}
                      isEnabled={isEnabled}
                      isSelected={selectedSettingId === setting.id}
                      status={isReplayingAllWorkflows ? status : 'idle'}
                      togglingScope={togglingScope === setting.id}
                      onToggleEnable={() => handleToggleEnable(setting.id, setting.enable)}
                      onToggleScope={() => handleToggleScope(setting.id, setting.scope || 'event')}
                      onClick={() => {
                        setSelectedSettingId(setting.id);
                        setSelectedSettingType(setting.type);
                        setIsSettingDialogOpen(true);
                      }}
                      onDelete={() => handleDeleteConfirm(setting.id)}
                      onMove={(direction) => handleMoveSetting(setting.id, direction)}
                      onNameSave={handleNameSave}
                      styles={styles}
                    />
                  </React.Fragment>
                );
              })}

              {/* Arrow before Add Action Button */}
              {selectedFlowSettings.length > 0 && (
                <WorkflowConnector />
              )}

              <Box sx={WORKFLOW_NODE_CONTAINER_SX}>
                <AppTooltip
                  title={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {t('workflow.add_new_action', 'Add Action')}
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.8 }}>
                        Click to add a new workflow action
                      </Typography>
                    </Box>
                  }
                  placement="top"
                  arrow
                >
                  <Box
                    onClick={handleOpenMenu}
                    aria-controls={menuOpen ? "add-new-menu" : undefined}
                    aria-haspopup="true"
                    aria-expanded={menuOpen ? "true" : undefined}
                    sx={{
                      backgroundColor: '#ffffff',
                      color: '#5757b0',
                      border: '3px dashed rgba(87, 87, 176, 0.4)',
                      background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                      borderRadius: '50%',
                      padding: '0px',
                      width: { xs: '60px', md: '80px' },
                      height: { xs: '60px', md: '80px' },
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      position: 'relative',
                      boxShadow: '0 2px 8px rgba(87, 87, 176, 0.15), inset 0 1px 2px rgba(255, 255, 255, 0.5)',
                      opacity: 1,
                      transform: 'scale(1)',

                      '&:hover': {
                        transform: 'translateY(-3px) scale(1.02)',
                        boxShadow: '0 8px 24px rgba(87, 87, 176, 0.25), 0 4px 8px rgba(87, 87, 176, 0.15)',
                        backgroundColor: '#f0f0ff',
                        border: '3px dashed rgba(87, 87, 176, 0.6)',
                        background: 'linear-gradient(135deg, #f0f0ff 0%, #e8eaf6 100%)',
                      },

                      '&:active': {
                        transform: 'translateY(0px) scale(0.98)',
                      }
                    }}
                  >
                    <Typography sx={{
                      fontSize: { xs: '24px', md: '32px' },
                      fontWeight: 'bold',
                      color: '#5757b0',
                      textShadow: '0 1px 2px rgba(87, 87, 176, 0.2)',
                      transition: 'all 0.3s ease'
                    }}>
                      +
                    </Typography>
                  </Box>
                </AppTooltip>

                {/* Text Label Below Circular Button */}
                <Box sx={{
                  mt: 1.5,
                  textAlign: 'center',
                  minHeight: '32px',
                  maxHeight: '44px', // Maximum height for 2 lines of text
                  maxWidth: { xs: '100px', md: '120px' }, // Maximum width to prevent text from becoming too wide
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden' // Hide overflow if text is still too long
                }}>
                  <Typography sx={{
                    fontSize: '12px',
                    fontWeight: 500,
                    textAlign: 'center',
                    color: '#5757b0',
                    width: '100%',
                    overflow: 'hidden',
                    display: '-webkit-box',
                    WebkitLineClamp: 2, // Allow maximum 2 lines of text
                    WebkitBoxOrient: 'vertical',
                    lineHeight: '1.2', // Tight line height for compact display
                    padding: '4px 6px',
                    borderRadius: '6px',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',

                    '&:hover': {
                      backgroundColor: 'rgba(87, 87, 176, 0.08)',
                      transform: 'scale(1.02)',
                    }
                  }}>
                    {t('workflow.add_new_action', 'Add Action')}
                  </Typography>
                </Box>
                <AddActionMenu
                  open={menuOpen}
                  onClose={handleCloseMenu}
                  onMenuItemSelect={handleMenuItemSelect}
                  pluginActions={pluginActions}
                  caseid={caseid}
                />
              </Box>
            </Box>
          </Box>
        )}
      </Box>
      {/* Removed old conditional rendering of settings components here */}

      {/* Dialog for Editing Settings - Now uses the new component */}
      <SettingsDialog
        open={isSettingDialogOpen && selectedSettingId !== null}
        onClose={handleSettingDialogClose}
        setting={currentSettingForDialog} // Pass the whole setting object
        settingType={selectedSettingType}
        tags={dialogTags}
        caseid={caseid}
        onSaveStatusChange={handleSettingSaveStatusChange}
        onScopeChange={handleScopeChange}
        isScopeChanging={togglingScope === selectedSettingId}
        pluginActions={pluginActions}
        workflowFlows={sortedFlows}
        t={t} // Pass translation function
      />

      <WorkflowFlowAutomationDialog
        open={flowAutomationDialogOpen}
        mode={flowAutomationDialogMode}
        flowName={flowAutomationDialogName}
        triggerMode={flowAutomationDialogTriggerMode}
        frequencyValue={flowAutomationDialogFrequencyValue}
        frequencyUnit={flowAutomationDialogFrequencyUnit}
        error={flowAutomationDialogError}
        onClose={handleCloseFlowAutomationDialog}
        onSubmit={handleSubmitFlowAutomationDialog}
        onFlowNameChange={(value) => {
          setFlowAutomationDialogName(value);
          setFlowAutomationDialogError('');
        }}
        onTriggerModeChange={(value) => {
          setFlowAutomationDialogTriggerMode(normalizeFlowTriggerMode(value));
          setFlowAutomationDialogError('');
        }}
        onFrequencyValueChange={(value) => {
          setFlowAutomationDialogFrequencyValue(value);
          setFlowAutomationDialogError('');
        }}
        onFrequencyUnitChange={(value) => {
          setFlowAutomationDialogFrequencyUnit(value);
          setFlowAutomationDialogError('');
        }}
        t={t}
      />

      <WorkflowFlowManageDialogs
        renameOpen={flowRenameDialogOpen}
        renameValue={flowRenameDialogValue}
        renameError={flowRenameDialogError}
        deleteOpen={flowDeleteDialogOpen}
        selectedFlowName={selectedFlow?.name || ''}
        onRenameClose={handleCloseFlowRenameDialog}
        onRenameValueChange={(value) => {
          setFlowRenameDialogValue(value);
          setFlowRenameDialogError('');
        }}
        onRenameSubmit={handleSubmitFlowRenameDialog}
        onDeleteClose={handleCloseFlowDeleteDialog}
        onDeleteSubmit={handleSubmitFlowDeleteDialog}
        t={t}
      />

      <Dialog
        open={templateDecisionDialogOpen}
        onClose={handleCloseTemplateDecisionDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {t('template.change_decision_title', 'This project is linked to a template')}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {t(
              'template.change_decision_message',
              `You changed ${templateDecisionData?.changeLabel || 'this action'}. Choose whether to keep the change only in this project or propagate it to all linked projects.`
            )}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {(templateDecisionData?.templateTitle || t('dashboard.template_fallback', 'Template'))}
            {templateDecisionData?.templateId ? ` (#${templateDecisionData.templateId})` : ''}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCancelTemplateDecision}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            variant="outlined"
            color="warning"
            onClick={() => handleTemplateDecision('detach')}
          >
            {t('template.detach_project', 'Disconnect Project')}
          </Button>
          <Button
            variant="contained"
            onClick={() => handleTemplateDecision('propagate')}
          >
            {t('template.update_template', 'Update Template')}
          </Button>
        </DialogActions>
      </Dialog>

      <WorkflowConfirmDialogs
        replayOpen={replayConfirmOpen}
        onReplayClose={handleReplayConfirmClose}
        onReplayConfirm={handleReplayAllWorkflows}
        deleteOpen={deleteConfirmOpen}
        onDeleteClose={handleDeleteCancel}
        onDeleteConfirm={handleDeleteExecute}
        t={t}
      />

      {/* Workflow Error Notification */}
      <Snackbar
        open={!!workflowError}
        autoHideDuration={8000}
        onClose={() => setWorkflowError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setWorkflowError(null)}
          severity="error"
          variant="filled"
          sx={{
            width: '100%',
            maxWidth: '600px',
            boxShadow: '0 4px 12px rgba(244, 67, 54, 0.3)',
          }}
        >
          <Box>
            <Box sx={{ fontWeight: 'bold', marginBottom: 1, fontSize: '1.1em' }}>
              Workflow Step Failed
            </Box>
            {workflowError && (
              <>
                <Box sx={{ fontSize: '0.95em', marginBottom: 0.5 }}>
                  <strong>Step:</strong> {workflowError.stepName} ({workflowError.stepIndex}/{workflowError.totalSteps})
                </Box>
                <Box sx={{ fontSize: '0.95em', marginBottom: 0.5 }}>
                  <strong>Error:</strong> {workflowError.errorMessage}
                </Box>
                <Box sx={{ fontSize: '0.85em', opacity: 0.9, marginTop: 1, fontStyle: 'italic' }}>
                  Remaining workflows were not executed
                </Box>
              </>
            )}
          </Box>
        </Alert>
      </Snackbar>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ActionsDataflowList;

